import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import GroupMembers from './GroupMembers';
import GameMatch from './GameMatch';
import AddCourt from './AddCourt';
import CourtManager from './CourtManager';
import PlayerManager from './PlayerManager';
import useMobileViewportZoom from './useMobileViewportZoom';
import { API_BASE } from "./config";
import socket from "../socket/socket";
import useGroupSocket from '../socket/useGroupSocket';
import GroupJoinQrModal from './GroupJoinQrModal';
import AdminSidebar from './AdminSidebar';
import AdminMatchRequestInbox from './AdminMatchRequestInbox';
import { apiFetch } from '../utils/apiFetch';
import { invalidateAdminAlerts, loadAdminAlerts } from '../services/adminAlertsApi';
import '../css/Dashboard.css';

const formatGroupDate = value => {
  if (!value) return 'ไม่ระบุวันที่';

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split('-');

  if (!year || !month || !day) return String(value);
  return `${day}/${month}/${year}`;
};

const AUTO_REFRESH_INTERVAL_SECONDS = 300;

const positionDashboardPanelBeside = (button, preferredWidth = 430, preferredHeight = 620) => {
  const rect = button.getBoundingClientRect();
  const width = Math.min(preferredWidth, window.innerWidth - 16);
  const height = Math.min(preferredHeight, window.innerWidth <= 700 ? window.innerHeight * 0.38 : window.innerHeight - 16);
  const clamp = (x, y) => ({
    x: Math.max(8, Math.min(window.innerWidth - width - 8, x)),
    y: Math.max(8, Math.min(window.innerHeight - height - 8, y))
  });
  if (window.innerWidth <= 700) {
    const below = rect.bottom + 8;
    return clamp(8, below + height <= window.innerHeight - 8 ? below : rect.top - height - 8);
  }
  if (rect.right + 10 + width <= window.innerWidth - 8) return clamp(rect.right + 10, rect.top);
  if (rect.left - 10 - width >= 8) return clamp(rect.left - width - 10, rect.top);
  return clamp(rect.left, rect.bottom + 8);
};

const formatAutoRefreshCountdown = seconds => {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
};

const MENU_MODAL_DETAILS = {
  groups: {
    eyebrow: 'GROUP CONTROL',
    title: '📁 จัดการ Groups',
    description: 'สร้าง เลือก และจัดการ Group โดยไม่ต้องเปลี่ยนหน้า'
  },
  members: {
    eyebrow: 'MEMBER CONTROL',
    title: '👥 จัดการสมาชิก',
    description: 'ตรวจสอบสมาชิกและ Check-out ผู้เล่นใน Group ที่เลือก'
  },
  court: {
    eyebrow: 'COURT CONTROL',
    title: '🏟 จัดการ Court',
    description: 'เพิ่ม ลบ Check-in และ Check-out ได้โดยไม่ต้องเปลี่ยนหน้า'
  },
  players: {
    eyebrow: 'PLAYER CONTROL',
    title: '🧑 จัดการผู้เล่น',
    description: 'ค้นหา แก้ไขสถานะ และจัดการข้อมูลผู้เล่น'
  }
};

function Dashboard() {
  useMobileViewportZoom();

  const navigate = useNavigate();
  const location = useLocation();

  const [menu, setMenu] = useState('groups');
  const [groups, setGroups] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [groupDate, setGroupDate] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [updatingGroupStatusId, setUpdatingGroupStatusId] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [qrGroup, setQrGroup] = useState(null);
  const [monitorQrGroup, setMonitorQrGroup] = useState(null);
  const [refreshingDashboard, setRefreshingDashboard] = useState(false);
  const [dashboardRefreshVersion, setDashboardRefreshVersion] = useState(0);
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(AUTO_REFRESH_INTERVAL_SECONDS);
  const [adminAlerts, setAdminAlerts] = useState({ matchRequests: [], waitAlerts: [] });
  const [alertBusyId, setAlertBusyId] = useState(null);
  const [adminInboxOpen, setAdminInboxOpen] = useState(false);
  const [adminInboxButtonPosition, setAdminInboxButtonPosition] = useState(null);
  const [adminInboxPosition, setAdminInboxPosition] = useState(() => {
    try { return JSON.parse(localStorage.getItem('dashboard_inbox_position') || 'null'); }
    catch { return null; }
  });
  const adminInboxDragRef = useRef(null);
  const adminInboxButtonDragRef = useRef(null);
  const adminInboxButtonMovedRef = useRef(false);
  // 🔥 realtime state
  const [courts, setCourts] = useState([]);

  const user = useMemo(
    () => JSON.parse(localStorage.getItem('user')),
    []
  );
  const dashboardAction = location.state?.dashboardAction;
  const dashboardGroupId = location.state?.selectedGroupId;

  /* ===============================
     AUTH CHECK
  =============================== */
  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!activeModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        setActiveModal(null);
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeModal]);

  /* ===============================
     LOAD GROUPS
  =============================== */
  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroupId) localStorage.setItem('last_group_id', String(selectedGroupId));
  }, [selectedGroupId]);

  /* ===============================
     REALTIME GROUP DELETE
  =============================== */
  useEffect(() => {
    const handleGroupDeleted = data => {
      const deletedGroupId = Number(data.groupId);

      setGroups(currentGroups =>
        currentGroups.filter(group => Number(group.id) !== deletedGroupId)
      );

      setSelectedGroupId(currentGroupId => {
        if (Number(currentGroupId) === deletedGroupId) {
          setMenu('groups');
          setActiveModal(null);
          return null;
        }

        return currentGroupId;
      });
    };

    const handleGroupStatusUpdated = data => {
      const changedGroupId = Number(data.groupId ?? data.group_id);
      const nextStatus = data.Group_Status || data.status;
      if (!changedGroupId || !nextStatus) return;

      setGroups(currentGroups => currentGroups.map(group =>
        Number(group.id) === changedGroupId
          ? { ...group, Group_Status: nextStatus }
          : group
      ));
    };

    socket.on('group-deleted', handleGroupDeleted);
    socket.on('group-closed', handleGroupStatusUpdated);
    socket.on('group-opened', handleGroupStatusUpdated);
    socket.on('member-joined', fetchGroups);
    socket.on('member-updated', fetchGroups);
    socket.on('member-checkout', fetchGroups);
    socket.on('player-status-updated', fetchGroups);

    return () => {
      socket.off('group-deleted', handleGroupDeleted);
      socket.off('group-closed', handleGroupStatusUpdated);
      socket.off('group-opened', handleGroupStatusUpdated);
      socket.off('member-joined', fetchGroups);
      socket.off('member-updated', fetchGroups);
      socket.off('member-checkout', fetchGroups);
      socket.off('player-status-updated', fetchGroups);
    };
  }, []);

  const fetchGroups = async () => {
    const res = await apiFetch(`${API_BASE}/api/groups`);
    const data = await res.json();
    const loadedGroups = Array.isArray(data) ? data : [];
    setGroups(loadedGroups.map(group => ({
      ...group,
      total_members: Number(group.total_members || 0)
    })));
  };

  const openGroupCheckinPage = group => {
    navigate(`/admin/checkin/${encodeURIComponent(group.id)}`);
  };

  const fetchAdminAlerts = async groupId => {
    if (!groupId) return setAdminAlerts({ matchRequests: [], waitAlerts: [] });
    try {
      const result = await loadAdminAlerts(groupId);
      setAdminAlerts({ matchRequests: result.matchRequests || [], waitAlerts: result.waitAlerts || [] });
    } catch (alertError) { console.error('LOAD ADMIN ALERTS ERROR:', alertError); }
  };

  const resolveWaitAlert = async alertId => {
    setAlertBusyId(Number(alertId));
    try {
      const response = await apiFetch(`${API_BASE}/api/match-planner/${Number(selectedGroupId)}/wait-alerts/${Number(alertId)}/resolve`, {
        method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` }
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || 'บันทึกรับทราบไม่สำเร็จ');
      invalidateAdminAlerts(selectedGroupId);
      await fetchAdminAlerts(selectedGroupId);
    } catch (alertError) { window.alert(alertError.message); }
    finally { setAlertBusyId(null); }
  };

  useEffect(() => {
    if (adminInboxPosition) localStorage.setItem('dashboard_inbox_position', JSON.stringify(adminInboxPosition));
  }, [adminInboxPosition]);

  const moveAdminInbox = event => {
    const drag = adminInboxDragRef.current;
    if (!drag) return;
    setAdminInboxPosition({
      x: Math.max(8, Math.min(window.innerWidth - drag.width - 8, drag.left + event.clientX - drag.pointerX)),
      y: Math.max(8, Math.min(window.innerHeight - drag.height - 8, drag.top + event.clientY - drag.pointerY))
    });
  };

  const finishAdminInboxDrag = event => {
    if (!adminInboxDragRef.current) return;
    adminInboxDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const moveAdminInboxButton = event => {
    const drag = adminInboxButtonDragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.pointerX;
    const dy = event.clientY - drag.pointerY;
    if (Math.abs(dx) + Math.abs(dy) > 5) adminInboxButtonMovedRef.current = true;
    setAdminInboxButtonPosition({
      x: Math.max(8, Math.min(window.innerWidth - drag.width - 8, drag.left + dx)),
      y: Math.max(8, Math.min(window.innerHeight - drag.height - 8, drag.top + dy))
    });
  };

  const finishAdminInboxButtonDrag = event => {
    if (!adminInboxButtonDragRef.current) return;
    adminInboxButtonDragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  useEffect(() => {
    const keepInboxControlsVisible = () => {
      setAdminInboxButtonPosition(current => {
        if (!current) return current;
        const next = {
          x: Math.max(8, Math.min(window.innerWidth - 150, Number(current.x) || 8)),
          y: Math.max(8, Math.min(window.innerHeight - 58, Number(current.y) || 8))
        };
        return next.x === current.x && next.y === current.y ? current : next;
      });
      setAdminInboxPosition(current => {
        if (!current) return current;
        const next = {
          x: Math.max(8, Math.min(window.innerWidth - Math.min(430, window.innerWidth - 16) - 8, Number(current.x) || 8)),
          y: Math.max(8, Math.min(window.innerHeight - 120, Number(current.y) || 8))
        };
        return next.x === current.x && next.y === current.y ? current : next;
      });
    };
    window.addEventListener('resize', keepInboxControlsVisible);
    window.addEventListener('orientationchange', keepInboxControlsVisible);
    return () => {
      window.removeEventListener('resize', keepInboxControlsVisible);
      window.removeEventListener('orientationchange', keepInboxControlsVisible);
    };
  }, []);

  useEffect(() => {
    if (!selectedGroupId) {
      setAdminAlerts({ matchRequests: [], waitAlerts: [] });
      return undefined;
    }
    fetchAdminAlerts(selectedGroupId);
    const refreshAlerts = event => {
      if (!event?.groupId || Number(event.groupId) === Number(selectedGroupId)) {
        fetchAdminAlerts(selectedGroupId);
      }
    };
    socket.on('admin-alert-updated', refreshAlerts);
    socket.on('match-planner-updated', refreshAlerts);
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') fetchAdminAlerts(selectedGroupId);
    }, 30000);
    return () => {
      window.clearInterval(intervalId);
      socket.off('admin-alert-updated', refreshAlerts);
      socket.off('match-planner-updated', refreshAlerts);
    };
  }, [selectedGroupId]);
  /* ===============================
     LOAD COURTS (Realtime Base)
  =============================== */
  const fetchCourts = async (groupId) => {
    if (!groupId) return;

    const res = await apiFetch(`${API_BASE}/api/courts/${groupId}`);
    const data = await res.json();
    setCourts(data);
  };

  const refreshDashboard = async () => {
    if (refreshingDashboard) return;

    try {
      setRefreshingDashboard(true);
      await Promise.all([
        fetchGroups(),
        selectedGroupId ? fetchCourts(selectedGroupId) : Promise.resolve()
      ]);
      setDashboardRefreshVersion(version => version + 1);
      setAutoRefreshSeconds(AUTO_REFRESH_INTERVAL_SECONDS);
    } catch (error) {
      console.error('REFRESH DASHBOARD ERROR:', error);
    } finally {
      setRefreshingDashboard(false);
    }
  };

  const refreshDashboardRef = useRef(refreshDashboard);
  refreshDashboardRef.current = refreshDashboard;

  // Refresh the dashboard periodically so player status stays in sync after
  // a Match is created or started from another Dashboard/monitor session.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setAutoRefreshSeconds(current => {
        if (current <= 1) {
          refreshDashboardRef.current();
          return AUTO_REFRESH_INTERVAL_SECONDS;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [selectedGroupId]);

  /* ===============================
     SOCKET LISTENER (สำคัญมาก)
  =============================== */
 useGroupSocket(selectedGroupId);

 useEffect(() => {

  if (!selectedGroupId) return;

  fetchCourts(selectedGroupId);

  const handleCourtUpdate = (data) => {
    console.log("🔥 Court Event:", data);

    if (Number(data.groupId) === Number(selectedGroupId)) {
      fetchCourts(selectedGroupId);
    }
  };

  /* ===== Court Events ===== */
  socket.on("courts-added", handleCourtUpdate);
  socket.on("court-status-updated", handleCourtUpdate);
  socket.on("court-deleted", handleCourtUpdate);
  socket.on("courts-initialized", handleCourtUpdate);

  return () => {

    socket.off("courts-added", handleCourtUpdate);
    socket.off("court-status-updated", handleCourtUpdate);
    socket.off("court-deleted", handleCourtUpdate);
    socket.off("courts-initialized", handleCourtUpdate);

  };

}, [selectedGroupId]);

  /* ===============================
     CREATE GROUP
  =============================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const res = await apiFetch(`${API_BASE}/api/groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        Group_Name: groupName,
        Group_Date: groupDate
      })
    });

    const data = await res.json();
    if (!res.ok) return alert(data.message);

    setGroupName('');
    setGroupDate('');
    fetchGroups();
  };

  /* ===============================
     DELETE GROUP
  =============================== */
  const handleDeleteGroup = async group => {
    if (deletingGroupId !== null) return;

    const confirmed = window.confirm(
      `ต้องการลบ Group "${group.Group_Name}" ใช่หรือไม่?\n\n` +
      'สมาชิกและสนามที่อยู่ใน Group นี้จะถูกนำออกด้วย และไม่สามารถย้อนกลับได้'
    );

    if (!confirmed) return;

    try {
      setDeletingGroupId(group.id);

      const res = await apiFetch(`${API_BASE}/api/groups/${group.id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || 'ไม่สามารถลบ Group ได้');
      }

      setGroups(currentGroups =>
        currentGroups.filter(item => Number(item.id) !== Number(group.id))
      );

      if (Number(selectedGroupId) === Number(group.id)) {
        setSelectedGroupId(null);
        setMenu('groups');
        setActiveModal(null);
      }

      alert(data.message || 'ลบ Group สำเร็จ');
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setDeletingGroupId(null);
    }
  };

  const handleToggleGroupStatus = async group => {
    if (updatingGroupStatusId !== null) return;

    const isClosed = ['CLOSE', 'CLOSED'].includes(
      String(group.Group_Status || '').toUpperCase()
    );
    const nextStatus = isClosed ? 'OPEN' : 'CLOSED';
    const actionLabel = isClosed ? 'เปิด' : 'ปิด';

    if (!window.confirm(`${actionLabel} Group "${group.Group_Name}" ใช่หรือไม่?`)) return;

    try {
      setUpdatingGroupStatusId(group.id);
      const token = localStorage.getItem('token');
      const res = await apiFetch(`${API_BASE}/api/groups/${group.id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data.message || `ไม่สามารถ${actionLabel} Group ได้`);

      setGroups(currentGroups => currentGroups.map(item =>
        Number(item.id) === Number(group.id)
          ? { ...item, Group_Status: data.Group_Status || nextStatus }
          : item
      ));
      alert(data.message || `${actionLabel} Group สำเร็จ`);
    } catch (error) {
      alert(`❌ ${error.message}`);
    } finally {
      setUpdatingGroupStatusId(null);
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate('/');
  };

  const openMenuModal = (modalName, groupId) => {
    const targetGroupId = groupId || selectedGroupId;
    const needsGroup = ['members', 'court'].includes(modalName);

    if (!MENU_MODAL_DETAILS[modalName]) return;

    if (needsGroup && !targetGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดเมนูนี้');
      return;
    }

    if (
      targetGroupId &&
      Number(targetGroupId) !== Number(selectedGroupId)
    ) {
      setSelectedGroupId(targetGroupId);
    }

    if (modalName === 'court') {
      fetchCourts(targetGroupId);
    }

    setActiveModal(modalName);
  };

  const openMatchPage = groupId => {
    const targetGroupId = groupId || selectedGroupId;

    if (!targetGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดหน้าจัด Match');
      return;
    }

    setSelectedGroupId(Number(targetGroupId));
    localStorage.setItem('last_group_id', String(targetGroupId));
    navigate(`/game-match-control/${Number(targetGroupId)}`);
  };

  const openMonitorPage = () => {
    if (!selectedGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดหน้า Monitor');
      return;
    }

    setActiveModal(null);
    const monitorUrl = `${window.location.origin}/admin_monitor/${selectedGroupId}`;
    const monitorWindow = window.open(monitorUrl, '_blank');

    // หาก Browser บล็อก Pop-up ให้เปิดในหน้าเดิมแทน
    if (monitorWindow) {
      monitorWindow.opener = null;
    } else {
      window.location.assign(monitorUrl);
    }
  };

  const openMatchMonitorPage = () => {
    if (!selectedGroupId) {
      alert('กรุณาเลือก Group ก่อนเปิดหน้า Match Monitor');
      return;
    }

    setActiveModal(null);
    const monitorUrl = `${window.location.origin}/monitor`;
    const monitorWindow = window.open(monitorUrl, '_blank');

    if (monitorWindow) {
      monitorWindow.opener = null;
    } else {
      window.location.assign(monitorUrl);
    }
  };

  useEffect(() => {
    const action = dashboardAction;
    const requestedGroupId = Number(dashboardGroupId) || null;

    if (!action) return;

    if (requestedGroupId) {
      setSelectedGroupId(requestedGroupId);
    }

    if (action === 'match') {
      setActiveModal(null);
      setMenu('match');
    } else if (MENU_MODAL_DETAILS[action]) {
      if (action === 'court' && requestedGroupId) {
        fetchCourts(requestedGroupId);
      }

      setActiveModal(action);
    }

    navigate('/dashboard', { replace: true, state: null });
  }, [dashboardAction, dashboardGroupId, navigate]);

  const closeMenuModal = () => {
    const closingModal = activeModal;
    setActiveModal(null);

    if (closingModal === 'court' && selectedGroupId) {
      fetchCourts(selectedGroupId);
    }

  };

  const visibleGroups = groups
    .filter(group => String(group.Group_Name || '').toLowerCase().includes(groupSearch.trim().toLowerCase()))
    .sort((a, b) => {
      const aOpen = !['CLOSE', 'CLOSED'].includes(String(a.Group_Status || '').toUpperCase());
      const bOpen = !['CLOSE', 'CLOSED'].includes(String(b.Group_Status || '').toUpperCase());
      if (aOpen !== bOpen) return aOpen ? -1 : 1;
      return Number(b.id) - Number(a.id);
    });

  const selectGroupWorkspace = group => {
    openMatchPage(group.id);
  };

  const renderGroupsPanel = () => (
    <>
      <div className="card">
        <h3>สร้าง Group</h3>
        <form onSubmit={handleSubmit} className="form-stack">
          <input
            placeholder="Group Name"
            value={groupName}
            onChange={event => setGroupName(event.target.value)}
            required
          />
          <input
            type="date"
            value={groupDate}
            onChange={event => setGroupDate(event.target.value)}
          />
          <button className="primary-btn">สร้าง Group</button>
        </form>
      </div>

      <div className="card">
        <div className="group-list-heading">
          <div><h3>เลือกก๊วนเพื่อเริ่มจัดการ</h3><p>ก๊วนที่เปิดใช้งานจะแสดงก่อน เลือกแล้วระบบจะจำก๊วนล่าสุดให้</p></div>
          <input type="search" value={groupSearch} onChange={event => setGroupSearch(event.target.value)} placeholder="ค้นหาชื่อก๊วน..." aria-label="ค้นหาชื่อก๊วน" />
        </div>

        {visibleGroups.map(group => (
          <div key={group.id} className={`group-card ${Number(selectedGroupId) === Number(group.id) ? 'is-selected' : ''}`}>
            <div>
              <b>{group.Group_Name}</b>
              <div className="sub">
                <span
                  className={`group-status ${group.Group_Status || 'OPEN'}`}
                >
                  {group.Group_Status || 'OPEN'}
                </span>
                <span>📅 {formatGroupDate(group.Group_Date)}</span>
                <span className="group-total-members" title="สมาชิกทุกสถานะใน Group">
                  👥 ทั้งหมด {Number(group.total_members || 0)} คน
                </span>
              </div>
            </div>

            <div className="group-actions">
              <button type="button" className="group-enter-btn" title={`เลือกและเข้า Workspace ${group.Group_Name}`} onClick={() => selectGroupWorkspace(group)}>
                เข้า Workspace →
              </button>
              <button
                type="button"
                className="group-monitor-qr-btn"
                title={`สร้าง QR ดู Monitor สำหรับ ${group.Group_Name}`}
                aria-label={`สร้าง QR ดู Monitor สำหรับ ${group.Group_Name}`}
                onClick={() => setMonitorQrGroup(group)}
              >
                📺
              </button>
              <button
                type="button"
                className="group-qr-btn"
                title={`สร้าง QR Check-in สำหรับ ${group.Group_Name}`}
                aria-label={`สร้าง QR Check-in สำหรับ ${group.Group_Name}`}
                disabled={['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase())}
                onClick={() => setQrGroup(group)}
              >
                ▦
              </button>
              <button
                type="button"
                className="group-checkin-link-btn"
                title={`เปิดหน้า Check-in สำหรับ ${group.Group_Name}`}
                aria-label={`เปิดหน้า Check-in สำหรับ ${group.Group_Name}`}
                disabled={['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase())}
                onClick={() => openGroupCheckinPage(group)}
              >
                🔗
              </button>
              <button
                type="button"
                title={`${['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? 'เปิด' : 'ปิด'} Group ${group.Group_Name}`}
                aria-label={`${['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? 'เปิด' : 'ปิด'} Group ${group.Group_Name}`}
                disabled={Number(updatingGroupStatusId) === Number(group.id)}
                onClick={() => handleToggleGroupStatus(group)}
                style={{
                  color: ['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '#047857' : '#b45309',
                  background: ['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '#d1fae5' : '#fef3c7',
                  border: `1px solid ${['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '#6ee7b7' : '#fcd34d'}`,
                  opacity: Number(updatingGroupStatusId) === Number(group.id) ? 0.6 : 1,
                  cursor: Number(updatingGroupStatusId) === Number(group.id) ? 'wait' : 'pointer'
                }}
              >
                {Number(updatingGroupStatusId) === Number(group.id)
                  ? '⏳'
                  : ['CLOSE', 'CLOSED'].includes(String(group.Group_Status || '').toUpperCase()) ? '🔓' : '🔒'}
              </button>

              <button
                type="button"
                title={`จัดการสมาชิก Group ${group.Group_Name}`}
                aria-label={`จัดการสมาชิก Group ${group.Group_Name}`}
                onClick={() => openMenuModal('members', group.id)}
              >
                👥
              </button>

              <button
                type="button"
                title={`จัด Match Group ${group.Group_Name}`}
                aria-label={`จัด Match Group ${group.Group_Name}`}
                onClick={() => openMatchPage(group.id)}
              >
                🎲
              </button>

              <button
                type="button"
                title={`เปิดหน้าจัด Match แบบ Waiting Pair ของ ${group.Group_Name}`}
                aria-label={`เปิด Match Planner Group ${group.Group_Name}`}
                onClick={() => window.open(`/match-planner/${group.id}`, '_blank', 'noopener,noreferrer')}
                style={{ color: '#047857', background: '#d1fae5', border: '1px solid #6ee7b7' }}
              >
                ✨
              </button>

              <button
                type="button"
                title={`จัดการ Court ของ Group ${group.Group_Name}`}
                aria-label={`จัดการ Court ของ Group ${group.Group_Name}`}
                onClick={() => openMenuModal('court', group.id)}
              >
                🏟
              </button>

              <button
                type="button"
                title={`จัดการ Match End ของ Group ${group.Group_Name}`}
                aria-label={`จัดการ Match End ของ Group ${group.Group_Name}`}
                onClick={() => navigate(`/admin/group/${group.id}/matches`)}
              >
                🛡️
              </button>

              <button
                type="button"
                className="delete-group-btn"
                title={`ลบ Group ${group.Group_Name}`}
                aria-label={`ลบ Group ${group.Group_Name}`}
                disabled={Number(deletingGroupId) === Number(group.id)}
                onClick={() => handleDeleteGroup(group)}
                style={{
                  color: '#dc2626',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  opacity:
                    Number(deletingGroupId) === Number(group.id) ? 0.6 : 1,
                  cursor:
                    Number(deletingGroupId) === Number(group.id)
                      ? 'wait'
                      : 'pointer'
                }}
              >
                {Number(deletingGroupId) === Number(group.id) ? '⏳' : '🗑️'}
              </button>
            </div>
          </div>
        ))}
        {!visibleGroups.length && <div className="group-empty-state">ไม่พบก๊วนที่ค้นหา</div>}
      </div>
      {qrGroup && (
        <GroupJoinQrModal
          group={qrGroup}
          onClose={() => setQrGroup(null)}
        />
      )}
      {monitorQrGroup && (
        <GroupJoinQrModal
          group={monitorQrGroup}
          mode="monitor"
          onClose={() => setMonitorQrGroup(null)}
        />
      )}
    </>
  );

  const selectedGroup = groups.find(
    group => Number(group.id) === Number(selectedGroupId)
  );
  const plannerShortcutGroup = selectedGroup || groups.find(
    group => String(group.Group_Status || '').toUpperCase() === 'OPEN'
  );
  const activeModalDetails = activeModal
    ? MENU_MODAL_DETAILS[activeModal]
    : null;

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="dash-container admin-shell-with-sidebar">

      <AdminSidebar active="dashboard" groupId={selectedGroupId} groupName={selectedGroup?.Group_Name} />
      {selectedGroupId && <AdminMatchRequestInbox groupId={selectedGroupId} />}

      <div className="dash-header">
        <div className="dash-hero-copy">
          <small>🏸 NMBADMINTON ADMIN</small>
          <h1>Admin Dashboard</h1>
          <p>จัดการผู้เล่น แมตช์ และก๊วนได้อย่างง่ายดาย</p>
        </div>
        <div className="dash-summary-cards" aria-label="ข้อมูลภาพรวม">
          <article><span>👥</span><div><b>{groups.length}</b><small>ก๊วนทั้งหมด</small></div></article>
          <article><span>🏸</span><div><b>{groups.filter(group => String(group.Group_Status || '').toUpperCase() === 'OPEN').length}</b><small>ก๊วนที่เปิดอยู่</small></div></article>
          <article><span>⏱</span><div><b>{formatAutoRefreshCountdown(autoRefreshSeconds)}</b><small>อัปเดตถัดไป</small></div></article>
        </div>
        <div className="dash-header-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span className="dash-refresh-countdown" aria-live="polite">
            ⏱ รีเฟรชใน {formatAutoRefreshCountdown(autoRefreshSeconds)}
          </span>
          <button
            type="button"
            onClick={() => navigate('/admin/users')}
            style={{
              padding: '7px 12px', border: '1px solid #c4b5fd',
              borderRadius: 10, color: '#6d28d9', background: '#f5f3ff',
              fontWeight: 800, cursor: 'pointer'
            }}
          >
            🛡️ Admin ผู้เล่นทั้งหมด
          </button>
          <button
            type="button"
            onClick={() => selectedGroupId && navigate(`/admin/checkin/${selectedGroupId}`)}
            disabled={!selectedGroupId}
            style={{
              padding: '7px 12px', border: '1px solid #7dd3fc',
              borderRadius: 10, color: '#0369a1', background: '#f0f9ff',
              fontWeight: 800, cursor: 'pointer'
            }}
          >
            ▦ Check-in สมาชิก
          </button>
          <button
            type="button"
            onClick={() => selectedGroupId && navigate(`/admin/result-logs?groupId=${selectedGroupId}`)}
            disabled={!selectedGroupId}
            style={{
              padding: '7px 12px', border: '1px solid #fdba74',
              borderRadius: 10, color: '#c2410c', background: '#fff7ed',
              fontWeight: 800, cursor: 'pointer'
            }}
          >
            📝 Result Log
          </button>
          <button
            type="button"
            onClick={() => navigate('/rewards')}
            style={{
              padding: '7px 12px', border: '1px solid #f9a8d4',
              borderRadius: 10, color: '#be185d', background: '#fdf2f8',
              fontWeight: 800, cursor: 'pointer'
            }}
          >
            🎁 Admin Reward
          </button>
          <button
            type="button"
            disabled={!plannerShortcutGroup}
            onClick={() => plannerShortcutGroup && window.open(
              `/match-planner/${plannerShortcutGroup.id}`,
              '_blank',
              'noopener,noreferrer'
            )}
            title={plannerShortcutGroup
              ? `เปิด Match Planner: ${plannerShortcutGroup.Group_Name}`
              : 'ยังไม่มี Group เปิดอยู่'}
            style={{
              padding: '7px 12px', border: '1px solid #6ee7b7',
              borderRadius: 10, color: '#047857', background: '#ecfdf5',
              fontWeight: 800,
              cursor: plannerShortcutGroup ? 'pointer' : 'not-allowed',
              opacity: plannerShortcutGroup ? 1 : 0.55
            }}
          >
            ✨ Match Planner
          </button>
          <button
            type="button"
            onClick={refreshDashboard}
            disabled={refreshingDashboard}
            aria-label="รีเฟรชข้อมูล Dashboard"
            style={{
              padding: '7px 12px', border: '1px solid #93c5fd',
              borderRadius: 10, color: '#1d4ed8', background: '#eff6ff',
              fontWeight: 800, cursor: refreshingDashboard ? 'wait' : 'pointer',
              opacity: refreshingDashboard ? 0.65 : 1
            }}
          >
            {refreshingDashboard ? '⏳ กำลังรีเฟรช...' : '🔄 Refresh'}
          </button>
        </div>
      </div>

      <section className="dash-mobile-quick-actions" aria-label="เมนูใช้งานด่วน">
        <button type="button" onClick={() => selectedGroupId && navigate(`/admin/checkin/${selectedGroupId}`)} disabled={!selectedGroupId}><span>👥</span><b>Check-in สมาชิก</b><i>›</i></button>
        <button type="button" onClick={() => selectedGroupId && navigate(`/admin/result-logs?groupId=${selectedGroupId}`)} disabled={!selectedGroupId}><span>📝</span><b>ผลการแข่งขัน</b><i>›</i></button>
        <button type="button" onClick={() => navigate('/rewards')}><span>🎁</span><b>จัดการรางวัล</b><i>›</i></button>
        <button type="button" disabled={!plannerShortcutGroup} onClick={() => plannerShortcutGroup && window.open(`/match-planner/${plannerShortcutGroup.id}`, '_blank', 'noopener,noreferrer')}><span>✨</span><b>วางแผนจัดแมตช์</b><i>›</i></button>
      </section>

      <button
        type="button"
        className={`dashboard-inbox-button${adminAlerts.waitAlerts.length === 0 ? ' is-empty' : ''}`}
        style={adminInboxButtonPosition ? { left: adminInboxButtonPosition.x, top: adminInboxButtonPosition.y, right: 'auto', bottom: 'auto' } : undefined}
        onPointerDown={event => {
          const rect = event.currentTarget.getBoundingClientRect();
          adminInboxButtonMovedRef.current = false;
          adminInboxButtonDragRef.current = { pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={moveAdminInboxButton}
        onPointerUp={finishAdminInboxButtonDrag}
        onPointerCancel={finishAdminInboxButtonDrag}
        onClick={event => {
          if (adminInboxButtonMovedRef.current) { adminInboxButtonMovedRef.current = false; return; }
          setAdminInboxPosition(positionDashboardPanelBeside(event.currentTarget));
          setAdminInboxOpen(open => !open);
        }}
        title="กดเพื่อเปิด • ลากเพื่อย้ายตำแหน่ง"
      >
        <span className="dashboard-inbox-icon" aria-hidden="true">🔔</span><span>ข้อความ</span>
        {adminAlerts.waitAlerts.length > 0 && <b>{adminAlerts.waitAlerts.length}</b>}
      </button>

      {selectedGroupId && adminInboxOpen && (
        <aside
          className="dashboard-admin-alerts"
          role="status"
          aria-label="คำขอจากผู้เล่น"
          style={adminInboxPosition ? { left: adminInboxPosition.x, top: adminInboxPosition.y, right: 'auto' } : undefined}
          onPointerDown={event => {
            if (event.target.closest('button') || !event.target.closest('header')) return;
            const rect = event.currentTarget.getBoundingClientRect();
            adminInboxDragRef.current = { pointerX: event.clientX, pointerY: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }}
          onPointerMove={moveAdminInbox}
          onPointerUp={finishAdminInboxDrag}
          onPointerCancel={finishAdminInboxDrag}
        >
          <header><div><b>💬 กล่องข้อความผู้เล่น</b><span>{adminAlerts.waitAlerts.length} รายการ</span></div><button type="button" onClick={() => setAdminInboxOpen(false)}>×</button></header>
          {adminAlerts.waitAlerts.length === 0 && <p className="dashboard-inbox-empty">ยังไม่มีข้อความใหม่</p>}
          {adminAlerts.waitAlerts.map(alert => (
            <article className="is-wait-alert" key={`wait-${alert.id}`}>
              <div><b>⏰ {alert.requester_name}</b><span>“{alert.note || 'แจ้งว่ารอนาน'}”</span></div>
              <button type="button" disabled={alertBusyId === Number(alert.id)} onClick={() => resolveWaitAlert(alert.id)}>{alertBusyId === Number(alert.id) ? 'กำลังบันทึก…' : '✓ รับทราบ'}</button>
            </article>
          ))}
        </aside>
      )}

      <div className="dash-content">

        {menu === 'groups' && renderGroupsPanel()}

        {menu === 'members' && selectedGroupId && (
          <GroupMembers key={`members-${selectedGroupId}-${dashboardRefreshVersion}`} groupId={selectedGroupId} />
        )}

        {menu === 'match' && selectedGroupId && (
          <GameMatch key={`match-${selectedGroupId}-${dashboardRefreshVersion}`} groupId={selectedGroupId} />
        )}

        {menu === 'court' && selectedGroupId && (
          <>
            <AddCourt key={`add-court-${selectedGroupId}-${dashboardRefreshVersion}`} groupId={selectedGroupId} />
            <CourtManager
              key={`court-${selectedGroupId}-${dashboardRefreshVersion}`}
              groupId={selectedGroupId}
              courts={courts}   // 🔥 ส่ง realtime ลงไป
            />
          </>
        )}

        {menu === 'players' && (
          <PlayerManager key={`players-${dashboardRefreshVersion}`} />
        )}

      </div>

      <div className="bottom-nav-dash">
        <button
          type="button"
          className={activeModal === 'groups' ? 'is-active' : ''}
          title="เปิดเมนู Groups"
          aria-label="เปิดหน้าต่างจัดการ Groups"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'groups'}
          onClick={() => openMenuModal('groups')}
        >
          📁
        </button>
        <button
          type="button"
          className={activeModal === 'members' ? 'is-active' : ''}
          title="เปิดเมนูสมาชิก"
          aria-label="เปิดหน้าต่างจัดการสมาชิก"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'members'}
          onClick={() => openMenuModal('members')}
        >
          👥
        </button>
        <button
          type="button"
          className={!activeModal && menu === 'match' ? 'is-active' : ''}
          title="กลับหน้าจัด Match"
          aria-label="กลับหน้าจัด Match"
          onClick={() => openMatchPage()}
        >
          🎲
        </button>
        <button
          type="button"
          className={activeModal === 'court' ? 'is-active' : ''}
          title="เปิดเมนู Court"
          aria-label="เปิดหน้าต่างจัดการ Court"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'court'}
          onClick={() => openMenuModal('court')}
        >
          🏟
        </button>
        <button
          type="button"
          className={activeModal === 'players' ? 'is-active' : ''}
          title="เปิดเมนูผู้เล่น"
          aria-label="เปิดหน้าต่างจัดการผู้เล่น"
          aria-haspopup="dialog"
          aria-expanded={activeModal === 'players'}
          onClick={() => openMenuModal('players')}
        >
          🧑
        </button>
        <button
          type="button"
          title="เปิดหน้า Admin Monitor ในแท็บใหม่"
          aria-label="เปิดหน้า Admin Monitor ในแท็บใหม่"
          onClick={openMonitorPage}
        >
          📺
        </button>
        <button
          type="button"
          title="เปิดหน้า Match Monitor ในแท็บใหม่"
          aria-label="เปิดหน้า Match Monitor ในแท็บใหม่"
          onClick={openMatchMonitorPage}
        >
          🖥️
        </button>
        <button type="button" title="ออกจากระบบ" onClick={logout}>🚪</button>
      </div>

      {activeModal && activeModalDetails && createPortal(
        <div
          className="court-manager-modal-backdrop"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              closeMenuModal();
            }
          }}
        >
          <section
            className={`court-manager-modal dashboard-menu-modal is-${activeModal}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="dashboard-menu-modal-title"
          >
            <header className="court-manager-modal-header">
              <div>
                <span className="court-manager-modal-eyebrow">
                  {activeModalDetails.eyebrow}
                </span>
                <h2 id="dashboard-menu-modal-title">
                  {activeModalDetails.title}
                </h2>
                <p>
                  {activeModalDetails.description}
                  {['members', 'court'].includes(activeModal) &&
                    selectedGroup?.Group_Name &&
                    ` • Group: ${selectedGroup.Group_Name}`}
                </p>
              </div>

              <button
                type="button"
                className="court-manager-modal-close-icon"
                aria-label={`ปิด${activeModalDetails.title}`}
                title="ปิด"
                onClick={closeMenuModal}
              >
                ✕
              </button>
            </header>

            <div className="court-manager-modal-body dashboard-menu-modal-body">
              {activeModal === 'groups' && (
                <div className="dashboard-menu-modal-stack">
                  {renderGroupsPanel()}
                </div>
              )}

              {activeModal === 'members' && selectedGroupId && (
                <GroupMembers groupId={selectedGroupId} />
              )}

              {activeModal === 'court' && selectedGroupId && (
                <>
                  <AddCourt
                    groupId={selectedGroupId}
                    onChanged={() => fetchCourts(selectedGroupId)}
                  />
                  <CourtManager
                    groupId={selectedGroupId}
                    courts={courts}
                  />
                </>
              )}

              {activeModal === 'players' && <PlayerManager />}
            </div>

            <footer className="court-manager-modal-footer">
              <span>ข้อมูลจะอัปเดตโดยไม่เปลี่ยนหน้าหลัก</span>
              <button
                type="button"
                className="court-manager-modal-done"
                onClick={closeMenuModal}
              >
                ✓ จัดการเสร็จแล้ว — ปิด
              </button>
            </footer>
          </section>
        </div>,
        document.body
      )}


    </div>
  );
}

export default Dashboard;
